import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import ContentForm, { Content } from '../components/ContentForm';
import TableFilter from '../components/TableFilter';
import ActionButtons from '../components/ActionButtons';
import DetailsPanel from '../components/DetailsPanel';
import ReportModal from '../components/ReportModal';
import { useToast } from '../context/ToastContext';

const ContentsPage: React.FC = () => {
  const { addToast } = useToast();
  const [contents, setContents] = useState<Content[]>([]);
  const [filteredContents, setFilteredContents] = useState<Content[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingContent, setEditingContent] = useState<Content | null>(null);

  // Modal de Relatórios
  const [reportModal, setReportModal] = useState<{ isOpen: boolean; rowId?: number; isRowReport: boolean }>({
    isOpen: false,
    isRowReport: false
  });

  // Master-Detail State
  const [selectedContent, setSelectedContent] = useState<Content | null>(null);

  const fetchContents = useCallback(async () => {
    try {
      const response = await api.get('/contents/');
      setContents(response.data);
      setFilteredContents(response.data);
    } catch (error) {
      console.error("Error fetching contents", error);
    }
  }, []);

  useEffect(() => {
    fetchContents();
  }, [fetchContents]);

  const handleFilter = (filters: { [key: string]: string }) => {
    let result = contents;

    if (filters.title) {
      result = result.filter(c => c.title.toLowerCase().includes(filters.title.toLowerCase()));
    }
    if (filters.type) {
      result = result.filter(c => c.type === filters.type);
    }

    setFilteredContents(result);
  };

  const handleSuccess = () => {
    setShowForm(false);
    setEditingContent(null);
    fetchContents();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingContent(null);
  };

  const handleEdit = (content: Content) => {
    setEditingContent(content);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este conteúdo?')) {
      try {
        await api.delete(`/contents/${id}/`);
        addToast('Material excluído com sucesso.', 'success');
        fetchContents();
        if (selectedContent?.id === id) setSelectedContent(null);
      } catch (error) {
        console.error("Error deleting content", error);
        addToast('Erro ao excluir conteúdo.', 'error');
      }
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'Booklet': return <span className="badge badge-info">Cartilha</span>;
      case 'Text': return <span className="badge badge-secondary">Texto</span>;
      case 'Video': return <span className="badge badge-warning">Vídeo</span>;
      case 'Post': return <span className="badge badge-success">Post Social</span>;
      default: return <span className="badge">{type}</span>;
    }
  };

  const openGeneralReports = () => setReportModal({ isOpen: true, isRowReport: false });
  const openRowReport = (id: number) => setReportModal({ isOpen: true, rowId: id, isRowReport: true });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1>Conteúdos Educativos</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Materiais de apoio para as famílias</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={openGeneralReports}>Relatórios de Conteúdos</button>
          {!showForm && (
            <button className="btn" onClick={() => setShowForm(true)}>
              + Novo Conteúdo
            </button>
          )}
        </div>
      </div>

      {showForm ? (
        <div style={{ marginBottom: '30px' }}>
          <ContentForm
            onSuccess={handleSuccess}
            onCancel={handleCancel}
            contentToEdit={editingContent}
          />
        </div>
      ) : (
        <TableFilter
          filters={[
            { key: 'title', label: 'Título' },
            {
              key: 'type', label: 'Tipo', type: 'select', options: [
                { value: 'Booklet', label: 'Cartilha' },
                { value: 'Text', label: 'Texto' },
                { value: 'Video', label: 'Vídeo' },
                { value: 'Post', label: 'Post Social' }
              ]
            }
          ]}
          onFilter={handleFilter}
        />
      )}

      {reportModal.isOpen && (
        <ReportModal 
          isOpen={reportModal.isOpen}
          onClose={() => setReportModal({ ...reportModal, isOpen: false })}
          category="Content"
          rowId={reportModal.rowId}
          isRowReport={reportModal.isRowReport}
        />
      )}

      <div className="page-container">
        <div className="list-container card" style={{ overflowX: 'auto', padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th>Título</th>
                <th>Tipo</th>
                <th>Link</th>
                <th>Responsável</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredContents.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '30px', color: 'var(--text-light)' }}>
                    Nenhum conteúdo encontrado.
                  </td>
                </tr>
              ) : (
                filteredContents.map((content) => (
                  <tr
                    key={content.id}
                    onClick={() => setSelectedContent(content)}
                    className={selectedContent?.id === content.id ? 'selected' : ''}
                  >
                    <td style={{ fontWeight: '500' }}>{content.title}</td>
                    <td>{getTypeBadge(content.type)}</td>
                    <td>
                      {content.link ? (
                        <a href={content.link} target="_blank" rel="noreferrer" style={{ color: 'var(--primary-blue)', textDecoration: 'underline' }} onClick={(e) => e.stopPropagation()}>
                          Acessar Link
                        </a>
                      ) : (
                        <span style={{ color: 'var(--text-light)' }}>-</span>
                      )}
                    </td>
                    <td>{content.responsible_name || '-'}</td>
                    <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <ActionButtons
                        onReport={() => content.id && openRowReport(content.id)}
                        onEdit={() => handleEdit(content)}
                        onDelete={() => content.id && handleDelete(content.id)}
                        deletePermission="delete_content"
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {selectedContent && (
          <DetailsPanel title="Detalhes do Conteúdo" onClose={() => setSelectedContent(null)}>
            <div className="details-row">
              <div className="details-label">Título</div>
              <div className="details-value">{selectedContent.title}</div>
            </div>
            <div className="details-row">
              <div className="details-label">Tipo de Material</div>
              <div className="details-value">
                {getTypeBadge(selectedContent.type)}
              </div>
            </div>
            <div className="details-row">
              <div className="details-label">Responsável pela Produção</div>
              <div className="details-value">{selectedContent.responsible_name || '-'}</div>
            </div>
            <div className="details-row">
              <div className="details-label">Previsão de Conclusão</div>
              <div className="details-value">
                {selectedContent.expected_completion_date ? new Date(selectedContent.expected_completion_date).toLocaleDateString() : '-'}
              </div>
            </div>
            <div className="details-row">
              <div className="details-label">Data de Cadastro</div>
              <div className="details-value">
                {selectedContent.created_at ? new Date(selectedContent.created_at).toLocaleDateString() : '-'}
              </div>
            </div>
            <div className="details-row">
              <div className="details-label">Link Original</div>
              <div className="details-value">
                {selectedContent.link ? (
                  <a href={selectedContent.link} target="_blank" rel="noreferrer" style={{ color: 'var(--primary-blue)', wordBreak: 'break-all', textDecoration: 'underline' }}>
                    {selectedContent.link}
                  </a>
                ) : '-'}
              </div>
            </div>
            <div className="details-row">
              <div className="details-label">Resumo do Conteúdo</div>
              <div className="details-value" style={{ whiteSpace: 'pre-wrap' }}>
                {selectedContent.description || 'Sem descrição cadastrada.'}
              </div>
            </div>
          </DetailsPanel>
        )}
      </div>
    </div>
  );
};

export default ContentsPage;
